<script setup lang="ts">
// Kostra aplikace (SPEC.md 2.2): mobil = spodní lišta + mikrofon uprostřed, desktop = levá navigace 240 px.
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import {
  Briefcase,
  CalendarDays,
  CheckSquare,
  Handshake,
  Home,
  Menu,
  MessageCircle,
  Settings,
  ShieldCheck,
  Users,
  WifiOff,
  X,
} from 'lucide-vue-next'
import { useUiStore } from '@/stores/ui'
import { useAuthStore } from '@/stores/auth'
import { Button } from '@wac-buddy/ui'
import BrandLogo from '@/components/BrandLogo.vue'
import MicButton from '@/components/MicButton.vue'
import VoiceRecorder from '@/components/VoiceRecorder.vue'
import { dismissInstallBanner, promptInstall, showInstallBanner } from '@/lib/install'
import ProfileMenu from '@/components/ProfileMenu.vue'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const ui = useUiStore()

async function install() {
  await promptInstall()
}
const auth = useAuthStore()

const primaryNav = [
  { name: 'today', to: '/', labelKey: 'nav.today', icon: Home },
  { name: 'jobs', to: '/zakazky', labelKey: 'nav.jobs', icon: Briefcase },
  { name: 'clients', to: '/klienti', labelKey: 'nav.clients', icon: Users },
  { name: 'calendar', to: '/kalendar', labelKey: 'nav.calendar', icon: CalendarDays },
]
const baseSecondaryNav = [
  { name: 'promises', to: '/sliby', labelKey: 'nav.promises', icon: Handshake },
  { name: 'tasks', to: '/ukoly', labelKey: 'nav.tasks', icon: CheckSquare },
  { name: 'buddy', to: '/buddy', labelKey: 'nav.buddy', icon: MessageCircle },
  { name: 'settings', to: '/nastaveni', labelKey: 'nav.settings', icon: Settings },
]

const adminNav = { name: 'admin', to: '/admin', labelKey: 'nav.admin', icon: ShieldCheck }
const secondaryNav = computed(() =>
  auth.isAdmin ? [...baseSecondaryNav, adminNav] : baseSecondaryNav,
)

const appVersion = __APP_VERSION__
async function stopImpersonating() {
  const res = await auth.stopImpersonating()
  if (res.ok) await router.push('/admin')
}

const bare = computed(() => route.meta.bare === true)
const title = computed(() => (route.meta.titleKey ? t(route.meta.titleKey) : t('app.name')))
</script>

<template>
  <div v-if="bare" class="min-h-dvh bg-bg text-text">
    <slot />
  </div>

  <div v-else class="min-h-dvh bg-bg text-text lg:grid lg:grid-cols-[var(--sidebar-w)_1fr]">
    <!-- Desktop: levá navigace -->
    <nav
      class="hidden lg:flex lg:flex-col lg:gap-1 lg:border-r lg:border-border lg:bg-surface lg:p-4 lg:sticky lg:top-0 lg:h-dvh"
      :aria-label="t('nav.main')"
    >
      <RouterLink to="/" class="mb-4 flex min-h-tap items-center px-2 py-3 no-underline">
        <BrandLogo variant="horizontal" :height="28" :alt="t('app.name')" />
      </RouterLink>
      <RouterLink
        v-for="item in [...primaryNav, ...secondaryNav]"
        :key="item.name"
        :to="item.to"
        class="flex min-h-tap items-center gap-3 rounded-md px-3 text-text-2 no-underline hover:bg-surface-2"
        active-class="bg-primary-soft text-primary-text font-semibold"
      >
        <component :is="item.icon" :size="24" aria-hidden="true" />
        <span>{{ t(item.labelKey) }}</span>
      </RouterLink>
      <div class="mt-auto flex flex-col gap-2 overflow-y-auto border-t border-border-soft pt-3">
        <ProfileMenu collapsible />
        <div class="px-3 text-xs text-text-subtle">
          {{ t('app.version', { version: appVersion }) }}
        </div>
      </div>
    </nav>

    <div class="flex min-h-dvh flex-col">
      <div class="sticky top-0 z-20">
        <!-- Impersonace: wac_admin je v cizím účtu -->
        <div
          v-if="auth.impersonatedBy && auth.me"
          class="pt-safe flex flex-wrap items-center justify-between gap-2 border-b border-warning-border bg-warning-bg px-4 py-2 text-warning"
          role="status"
          data-testid="impersonation-banner"
        >
          <span>{{
            t('admin.impersonating', { name: auth.me.user.name, org: auth.me.org?.name ?? '' })
          }}</span>
          <Button variant="secondary" @click="stopImpersonating">{{ t('admin.stop') }}</Button>
        </div>

        <!-- Hlavička -->
        <header
          class="flex min-h-14 items-center justify-between gap-3 border-b border-border-soft bg-bg/90 px-4 backdrop-blur"
          :class="auth.impersonatedBy ? '' : 'pt-safe'"
        >
          <!-- Mobil: pod 640 px jen symbol (360 px: logo + název obrazovky + menu by se nevešly), od 640 px celé logo; od lg je logo v postranní liště. -->
          <div class="flex min-w-0 items-center gap-3">
            <RouterLink to="/" class="flex min-h-tap shrink-0 items-center no-underline lg:hidden">
              <BrandLogo variant="symbol" :height="32" :alt="t('app.name')" class="sm:hidden" />
              <BrandLogo
                variant="horizontal"
                :height="28"
                :alt="t('app.name')"
                class="hidden sm:inline-flex"
              />
            </RouterLink>
            <h1 class="m-0 truncate text-xl font-semibold">{{ title }}</h1>
          </div>
          <div class="flex items-center gap-2">
            <span
              v-if="!ui.online"
              class="flex items-center gap-1 rounded-pill border border-warning-border bg-warning-bg px-2 py-1 text-xs font-semibold text-warning"
              role="status"
            >
              <WifiOff :size="14" aria-hidden="true" />
              <span class="sr-only">{{ t('app.offline') }}</span>
            </span>
            <div class="hidden lg:block">
              <MicButton size="header" />
            </div>
            <button
              type="button"
              class="flex h-tap w-tap items-center justify-center rounded-md text-text-2 hover:bg-surface-2 lg:hidden"
              :aria-label="ui.menuOpen ? t('nav.close') : t('nav.more')"
              :aria-expanded="ui.menuOpen"
              @click="ui.menuOpen = !ui.menuOpen"
            >
              <X v-if="ui.menuOpen" :size="24" aria-hidden="true" />
              <Menu v-else :size="24" aria-hidden="true" />
            </button>
          </div>
        </header>
      </div>

      <!-- Mobil: menu „Více“ -->
      <nav
        v-if="ui.menuOpen"
        class="border-b border-border-soft bg-surface px-2 py-2 lg:hidden"
        :aria-label="t('nav.more')"
      >
        <RouterLink
          v-for="item in secondaryNav"
          :key="item.name"
          :to="item.to"
          class="flex min-h-tap items-center gap-3 rounded-md px-3 text-text no-underline"
          active-class="bg-primary-soft text-primary-text font-semibold"
          @click="ui.menuOpen = false"
        >
          <component :is="item.icon" :size="24" aria-hidden="true" />
          <span>{{ t(item.labelKey) }}</span>
        </RouterLink>
        <div class="mt-2 border-t border-border-soft pt-2">
          <ProfileMenu @navigate="ui.menuOpen = false" />
        </div>
      </nav>

      <p
        v-if="!ui.online"
        class="m-0 border-b border-warning-border bg-warning-bg px-4 py-2 text-sm text-warning"
      >
        {{ t('app.offline') }}
      </p>

      <!-- Instalace PWA (2.4): jen když prohlížeč instalaci nabízí a uživatel ji neodložil -->
      <div
        v-if="showInstallBanner"
        class="flex flex-wrap items-center gap-2 border-b border-border-soft bg-primary-soft px-4 py-2"
        role="region"
        :aria-label="t('install.title')"
        data-testid="install-banner"
      >
        <p class="m-0 flex-1 text-sm text-text-2">{{ t('install.banner') }}</p>
        <Button variant="secondary" @click="install">{{ t('install.action') }}</Button>
        <Button variant="ghost" @click="dismissInstallBanner">{{ t('install.later') }}</Button>
      </div>

      <!-- Obsah -->
      <main class="flex-1 px-4 pb-28 pt-4 lg:px-8 lg:pb-8">
        <slot />
      </main>

      <!-- Mobil: spodní lišta s mikrofonem uprostřed -->
      <nav
        class="pb-safe fixed inset-x-0 bottom-0 z-20 grid grid-cols-5 items-end border-t border-border-soft bg-surface lg:hidden"
        :aria-label="t('nav.main')"
      >
        <RouterLink
          v-for="(item, i) in primaryNav"
          :key="item.name"
          :to="item.to"
          class="flex min-h-14 flex-col items-center justify-center gap-0.5 text-xs text-text-muted no-underline"
          :class="i === 2 ? 'col-start-4' : ''"
          active-class="text-primary-text font-semibold"
        >
          <component :is="item.icon" :size="24" aria-hidden="true" />
          <span>{{ t(item.labelKey) }}</span>
        </RouterLink>
        <div class="col-start-3 row-start-1 flex justify-center">
          <MicButton class="-translate-y-4" />
        </div>
      </nav>

      <!-- Panel nahrávání hlasovky (otevře se mikrofonem) -->
      <VoiceRecorder />
    </div>
  </div>
</template>
